import { useCallback, useEffect, useState } from "react";
import {
  Scale, Building2, FileText, ShieldCheck, Plus, Send, Trash2, CheckCircle2, Clock, X, Paperclip, FileDown, Loader2,
} from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  "Active": "bg-emerald-50 text-emerald-600", "In Good Standing": "bg-emerald-50 text-emerald-600",
  "Pending Formation": "bg-amber-50 text-amber-700", "Needs Attention": "bg-red-50 text-red-600",
  "Dissolved": "bg-slate-100 text-slate-500", "Draft": "bg-slate-100 text-slate-500",
  "Pending Signature": "bg-amber-50 text-amber-700", "Executed": "bg-emerald-50 text-emerald-600",
  "Expired": "bg-red-50 text-red-600", "Not Started": "bg-slate-100 text-slate-500",
  "In Progress": "bg-blue-50 text-[#1a5fd0]", "Signed Off": "bg-emerald-50 text-emerald-600",
};

function Chip({ status }) {
  return <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[status] || "bg-slate-100 text-slate-500"}`}>{status}</span>;
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input {...props} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
    </label>
  );
}

function Select({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <select {...props} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="legal-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-head text-lg font-extrabold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Legal() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("entities");
  const [modal, setModal] = useState(null); // 'entity' | 'document'
  const [form, setForm] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const load = useCallback(() => { api.get("/legal/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { stats, entities, documents, compliance, options } = data;

  const openEntity = () => { setForm({ status: "Pending Formation", entity_type: "LLC" }); setModal("entity"); };
  const openDoc = () => { setForm({ doc_type: options.doc_types[0], status: "Draft", signers: "" }); setModal("document"); };

  const saveEntity = async () => {
    if (!form.legal_name?.trim()) return toast.error("Legal name is required.");
    try { await api.post("/legal/entities", form); toast.success("Entity added"); setModal(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save entity."); }
  };
  const saveDoc = async () => {
    if (!form.title?.trim()) return toast.error("Document title is required.");
    const payload = { ...form, signers: (form.signers || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try { await api.post("/legal/documents", payload); toast.success("Document added"); setModal(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save document."); }
  };
  const requestSig = async (id) => {
    try { const { data } = await api.post(`/legal/documents/${id}/request-signature`); toast.success(data.note); load(); }
    catch { toast.error("Could not request signature."); }
  };
  const markExecuted = async (id) => {
    try { await api.patch(`/legal/documents/${id}`, { status: "Executed" }); toast.success("Marked executed"); load(); }
    catch { toast.error("Could not update."); }
  };
  const delDoc = async (id) => { try { await api.delete(`/legal/documents/${id}`); load(); } catch { toast.error("Could not delete."); } };
  const uploadFile = async (id, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File too large (max 20MB).");
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/legal/documents/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload file."); }
    finally { setUploadingId(null); }
  };
  const viewFile = async (id) => {
    try {
      const res = await api.get(`/legal/documents/${id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open the file."); }
  };
  const delEntity = async (id) => { try { await api.delete(`/legal/entities/${id}`); load(); } catch { toast.error("Could not delete."); } };
  const setCompliance = async (id, status) => {
    try { await api.patch(`/legal/compliance/${id}`, { status }); load(); }
    catch { toast.error("Could not update."); }
  };

  const TABS = [["entities", "Entities", Building2], ["documents", "Documents", FileText], ["compliance", "Compliance", ShieldCheck]];

  return (
    <div className="animate-fade-up space-y-6" data-testid="legal-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Scale size={22} className="text-[#b0862f]" /> Legal &amp; Compliance</h1>
        <p className="mt-1 text-[13px] text-slate-500">Your business entities, legal documents, and compliance sign-off — all in one place.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Entities" value={stats.entities} tone="slate" />
        <Stat label="Documents" value={stats.documents} sub={`${stats.executed_documents} executed`} tone="blue" />
        <Stat label="Pending signatures" value={stats.pending_signatures} tone={stats.pending_signatures ? "red" : "slate"} />
        <Stat label="Compliance sign-off" value={`${stats.compliance_pct}%`} sub={`${stats.compliance_signed}/${stats.compliance_total} items`} tone="green" />
      </div>

      <div className="flex gap-5 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`legal-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* ENTITIES */}
      {tab === "entities" && (
        <div className="space-y-3" data-testid="legal-entities">
          <div className="flex justify-end">
            <button onClick={openEntity} data-testid="add-entity-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[13px] font-bold text-white hover:bg-[#1550b8]"><Plus size={15} /> Add entity</button>
          </div>
          {entities.length === 0 ? (
            <Card className="p-10 text-center text-[13px] text-slate-400">No entities yet. Add your first business entity.</Card>
          ) : entities.map((e) => (
            <Card key={e.id} className="p-4" data-testid="entity-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-head text-[15px] font-bold text-slate-900">{e.legal_name}</div>
                  <div className="mt-0.5 text-[12px] text-slate-500">{[e.entity_type, e.formation_state, e.formation_date].filter(Boolean).join(" · ") || "Details pending"}</div>
                  {(e.ein_last4 || e.registered_agent) && <div className="mt-1 text-[11px] text-slate-400">{e.ein_last4 ? `EIN ••••${e.ein_last4}` : ""}{e.registered_agent ? `  ·  Agent: ${e.registered_agent}` : ""}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Chip status={e.status} />
                  <button onClick={() => delEntity(e.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* DOCUMENTS */}
      {tab === "documents" && (
        <div className="space-y-3" data-testid="legal-documents">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-amber-700">E-signature delivery & file storage are <b>SANDBOX</b> until an e-sign provider and document storage are connected.</p>
            <button onClick={openDoc} data-testid="add-document-btn" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3.5 py-2 text-[13px] font-bold text-white hover:bg-[#1550b8]"><Plus size={15} /> Add document</button>
          </div>
          {documents.length === 0 ? (
            <Card className="p-10 text-center text-[13px] text-slate-400">No documents yet. Add operating agreements, authorizations, contracts and more.</Card>
          ) : documents.map((d) => (
            <Card key={d.id} className="p-4" data-testid="document-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-head text-[15px] font-bold text-slate-900">{d.title}</div>
                  <div className="mt-0.5 text-[12px] text-slate-500">{d.doc_type}{d.entity_name ? ` · ${d.entity_name}` : ""}</div>
                  {d.signers?.length > 0 && <div className="mt-1 text-[11px] text-slate-400">Signers: {d.signers.join(", ")}</div>}
                  {d.esign_status && d.esign_status !== "Not sent" && <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-amber-600">E-sign: {d.esign_status}</div>}
                  <div className="mt-2 flex items-center gap-3">
                    {d.file ? (
                      <button onClick={() => viewFile(d.id)} data-testid="view-file-btn" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a5fd0] hover:underline"><FileDown size={13} /> {d.file.filename || "View PDF"}</button>
                    ) : (
                      <label className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-slate-500 hover:text-[#1a5fd0]" data-testid="attach-file-label">
                        {uploadingId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />} {uploadingId === d.id ? "Uploading…" : "Attach PDF"}
                        <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="attach-file-input"
                          onChange={(e) => { uploadFile(d.id, e.target.files?.[0]); e.target.value = ""; }} />
                      </label>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <Chip status={d.status} />
                  <div className="flex items-center gap-2">
                    {d.status === "Pending Signature" && (
                      <button onClick={() => markExecuted(d.id)} data-testid="mark-executed-btn" className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600 hover:underline"><CheckCircle2 size={12} /> Mark executed</button>
                    )}
                    {d.status !== "Executed" && (
                      <button onClick={() => requestSig(d.id)} data-testid="request-signature-btn" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a5fd0] hover:underline"><Send size={12} /> Request signature</button>
                    )}
                    <button onClick={() => delDoc(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* COMPLIANCE */}
      {tab === "compliance" && (
        <div className="space-y-2" data-testid="legal-compliance">
          {compliance.map((c) => (
            <Card key={c.id} className="flex items-center justify-between gap-3 p-4" data-testid="compliance-item">
              <div className="flex items-center gap-3">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${c.status === "Signed Off" ? "bg-emerald-100 text-emerald-600" : c.status === "In Progress" ? "bg-blue-100 text-[#1a5fd0]" : "bg-slate-100 text-slate-400"}`}>
                  {c.status === "Signed Off" ? <CheckCircle2 size={16} /> : <Clock size={15} />}
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-slate-800">{c.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{c.category}{c.signed_off_by ? ` · signed off by ${c.signed_off_by}` : ""}</div>
                </div>
              </div>
              <select value={c.status} onChange={(e) => setCompliance(c.id, e.target.value)} data-testid="compliance-status-select"
                className="shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 outline-none focus:border-[#1a5fd0]">
                {options.compliance_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Card>
          ))}
        </div>
      )}

      {/* MODALS */}
      {modal === "entity" && (
        <Modal title="Add business entity" onClose={() => setModal(null)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Field label="Legal name" data-testid="entity-legal-name" value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Acme Holdings LLC" /></div>
            <Field label="Entity type" value={form.entity_type || ""} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} placeholder="LLC / S-Corp / C-Corp" />
            <Field label="Formation state" value={form.formation_state || ""} onChange={(e) => setForm({ ...form, formation_state: e.target.value })} placeholder="TX" />
            <Field label="Formation date" type="date" value={form.formation_date || ""} onChange={(e) => setForm({ ...form, formation_date: e.target.value })} />
            <Field label="EIN (last 4)" maxLength={4} value={form.ein_last4 || ""} onChange={(e) => setForm({ ...form, ein_last4: e.target.value })} placeholder="1234" />
            <Field label="Registered agent" value={form.registered_agent || ""} onChange={(e) => setForm({ ...form, registered_agent: e.target.value })} />
            <Select label="Status" options={options.entity_statuses} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} />
          </div>
          <button onClick={saveEntity} data-testid="save-entity-btn" className="mt-4 w-full rounded-lg bg-[#1a5fd0] py-2.5 text-[14px] font-bold text-white hover:bg-[#1550b8]">Save entity</button>
        </Modal>
      )}
      {modal === "document" && (
        <Modal title="Add legal document" onClose={() => setModal(null)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Field label="Title" data-testid="doc-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Operating Agreement — Acme Holdings LLC" /></div>
            <Select label="Type" options={options.doc_types} value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} />
            <Select label="Status" options={options.doc_statuses} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} />
            <Field label="Linked entity (name)" value={form.entity_name || ""} onChange={(e) => setForm({ ...form, entity_name: e.target.value })} placeholder="Acme Holdings LLC" />
            <Field label="Effective date" type="date" value={form.effective_date || ""} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
            <div className="sm:col-span-2"><Field label="Signers (comma separated)" value={form.signers || ""} onChange={(e) => setForm({ ...form, signers: e.target.value })} placeholder="Jane Owner, John Partner" /></div>
          </div>
          <button onClick={saveDoc} data-testid="save-document-btn" className="mt-4 w-full rounded-lg bg-[#1a5fd0] py-2.5 text-[14px] font-bold text-white hover:bg-[#1550b8]">Save document</button>
        </Modal>
      )}
    </div>
  );
}
